import { useCallback, useMemo } from "react";

import { Icon_Sidebar24, Sidebar as SidebarLib } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import type React from "react";
import { useLocation, useParams } from "react-router-dom";

import useNavigation from "../../hooks/navigation";
import type { WebExport } from "../../hooks/useWebExport";
import { Sidebar } from "../Sidebar/Sidebar";
import { Toolbar } from "../Toolbar/Toolbar";
import { ToolButton } from "../ToolButton/ToolButton";

import style from "./ModelViewerNavbar.module.css";

type ModelViewerNavbarProps = {
  open: boolean;
  setOpen: (value: boolean) => void;
  webExport: WebExport | undefined;
  isDrawingsLayout: boolean;
  isInDrawing: boolean;
  isCommentingEnabled: boolean;
};

const ModelViewerNavbar: React.FC<ModelViewerNavbarProps> = ({
  open,
  webExport,
  setOpen,
  isDrawingsLayout,
  isInDrawing,
  isCommentingEnabled,
}) => {
  const { viewLinkId = "", projectId = "" } = useParams();
  const navigation = useNavigation();
  const location = useLocation();

  const sortedDrawings = useMemo(() => {
    const drawings = webExport?.drawingsDownload ?? [];

    return [...drawings].sort((a, b) => (a.title > b.title ? 1 : a.title < b.title ? -1 : 0));
  }, [webExport?.drawingsDownload]);

  const navigationHandler = useCallback(() => {
    if (window.innerWidth < 481) {
      setOpen(!open);
    }
  }, [open, setOpen]);

  return (
    <Sidebar
      title="Project Outline"
      position="left"
      isOpen={open}
      onClose={() => setOpen(false)}
      controlButtons={
        <div className={style.sidebarWrapper}>
          {isDrawingsLayout && (
            <ToolButton
              alt="Project Outline"
              title="Project Outline"
              icon={<Icon_Sidebar24 />}
              onClick={() => setOpen?.(!open)}
              isActive={open}
            />
          )}
          {!isInDrawing && isCommentingEnabled && <Toolbar />}
        </div>
      }
    >
      <div className={style.navbarContent}>
        <SidebarLib.Header>Model</SidebarLib.Header>
        <SidebarLib.Item
          selected={location.pathname.endsWith(`/v/${viewLinkId}`)}
          onClick={() => {
            navigation.goToViewLink({ viewLinkId, projectId });
            navigationHandler();
          }}
          className={classNames(
            style.sidebarItem,
            location.pathname.endsWith(`/v/${viewLinkId}`) && style.active
          )}
        >
          <span className={classNames(style.item)}>{webExport?.title}</span>
        </SidebarLib.Item>
        <SidebarLib.Header>Drawings</SidebarLib.Header>
        {sortedDrawings.map((drawing) => (
          <SidebarLib.Item
            selected={location.pathname.endsWith(`/drawings/${drawing.drawingId}`)}
            onClick={() => {
              navigation.goToDrawing({ viewLinkId, projectId, drawingId: drawing.drawingId });
              navigationHandler();
            }}
            key={drawing.drawingId}
            className={classNames(
              style.sidebarItem,
              location.pathname.endsWith(`/drawings/${drawing.drawingId}`) && style.active
            )}
          >
            <span className={classNames(style.item)}>{drawing.title}</span>
          </SidebarLib.Item>
        ))}
      </div>
    </Sidebar>
  );
};

export default ModelViewerNavbar;
